import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { getDashboardPath } from '../lib/auth';
import {
  Baby,
  Sparkles,
  Shield,
  Zap,
  ArrowRight,
  Play,
  Star,
  Users,
  Image,
  Clock,
  ChevronRight
} from 'lucide-react';

export const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    // If authenticated, redirect to dashboard
    if (isAuthenticated && user) {
      navigate(getDashboardPath(user.role), { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const features = [
    {
      icon: Baby,
      title: 'Fetal Yüz Görüntüleme',
      description: 'Ultrason görüntülerinden bebeğinizin gerçekçi yüz tahminlerini oluşturun'
    },
    {
      icon: Sparkles,
      title: 'AI Destekli Teknoloji',
      description: 'En gelişmiş yapay zeka algoritmaları ile yüksek doğrulukta sonuçlar'
    },
    {
      icon: Shield,
      title: 'Güvenli & Gizli',
      description: 'Tüm verileriniz şifrelenmiş ve KVKK uyumlu sistemlerde saklanır'
    },
    {
      icon: Zap,
      title: 'Hızlı Sonuçlar',
      description: 'Saniyeler içinde profesyonel kalitede görüntüler elde edin'
    }
  ];

  const stats = [
    { value: '10,000+', label: 'Mutlu Aile' },
    { value: '500+', label: 'Klinik' },
    { value: '99.2%', label: 'Memnuniyet' },
    { value: '24/7', label: 'Destek' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#5A67D8] to-[#4338CA] flex items-center justify-center">
                <Baby className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                NeoBreed AI
              </span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-slate-900 transition-colors">Özellikler</a>
              <a href="/about" className="text-slate-600 hover:text-slate-900 transition-colors">Hakkımızda</a>
              <a href="/contact" className="text-slate-600 hover:text-slate-900 transition-colors">İletişim</a>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                onClick={() => navigate('/login')}
                className="text-slate-600"
                data-testid="nav-login-btn"
              >
                Giriş Yap
              </Button>
              <Button
                onClick={() => navigate('/register')}
                className="bg-[#5A67D8] hover:bg-[#4338CA] text-white"
                data-testid="nav-register-btn"
              >
                Ücretsiz Başla
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#F8F7FC] to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#E0D7FF] rounded-full">
                <Sparkles className="w-4 h-4 text-[#5A67D8]" />
                <span className="text-sm font-medium text-[#5A67D8]">Yapay Zeka Destekli</span>
              </div>
              <h1 
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Bebeğinizi
                <span className="text-[#5A67D8]"> Doğmadan </span>
                Görün
              </h1>
              <p className="text-lg text-slate-600 max-w-xl" style={{ fontFamily: 'Inter, sans-serif' }}>
                Ultrason görüntülerinden yapay zeka ile bebeğinizin gerçekçi yüz tahminlerini oluşturun. 
                Anne ve babalar için unutulmaz bir deneyim.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  onClick={() => navigate('/register')}
                  className="bg-[#5A67D8] hover:bg-[#4338CA] text-white h-14 px-8 text-lg"
                  data-testid="hero-cta-btn"
                >
                  Ücretsiz Deneyin
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="h-14 px-8 text-lg border-2"
                >
                  <Play className="w-5 h-5 mr-2" />
                  Nasıl Çalışır?
                </Button>
              </div>
              <div className="flex items-center gap-4 pt-4">
                <div className="flex -space-x-3">
                  {[1,2,3,4].map((i) => (
                    <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 border-2 border-white" />
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map((i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-sm text-slate-500">10,000+ mutlu aile</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl border-8 border-white">
                <div className="aspect-square bg-gradient-to-br from-[#E0D7FF] to-[#F8F7FC] flex items-center justify-center">
                  <div className="text-center p-8">
                    <Baby className="w-32 h-32 mx-auto text-[#5A67D8] mb-4" />
                    <p className="text-xl font-medium text-slate-600">AI ile Fetal Yüz Görüntüleme</p>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#5A67D8] rounded-3xl opacity-20 blur-2xl" />
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-[#E0D7FF] rounded-3xl opacity-60 blur-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl font-bold text-[#5A67D8]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {stat.value}
                </p>
                <p className="text-slate-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-[#F8F7FC]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Neden NeoBreed AI?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              En son yapay zeka teknolojisi ile anne-babalara unutulmaz bir deneyim sunuyoruz
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-white">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-[#E0D7FF] flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-[#5A67D8]" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800 mb-2">{feature.title}</h3>
                    <p className="text-slate-500 text-sm">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Nasıl Çalışır?
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '1', icon: Image, title: 'Ultrason Yükle', desc: 'Fetal ultrason görüntüsünü sisteme yükleyin' },
              { step: '2', icon: Sparkles, title: 'AI Analizi', desc: 'Yapay zeka görüntüyü analiz eder ve işler' },
              { step: '3', icon: Baby, title: 'Sonuç Al', desc: 'Bebeğinizin tahmini yüz görüntüsünü alın' }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="relative text-center">
                  <div className="w-16 h-16 rounded-full bg-[#5A67D8] text-white text-2xl font-bold flex items-center justify-center mx-auto mb-6">
                    {item.step}
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-[#E0D7FF] flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-[#5A67D8]" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-2">{item.title}</h3>
                  <p className="text-slate-500">{item.desc}</p>
                  {index < 2 && (
                    <ChevronRight className="hidden md:block absolute top-8 -right-4 w-8 h-8 text-slate-300" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#5A67D8] to-[#4338CA]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-3xl sm:text-4xl font-bold text-white mb-6"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Bebeğinizi Görmeye Hazır mısınız?
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Hemen ücretsiz hesap oluşturun ve ilk taramanızı yapın
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/register')}
            className="bg-white text-[#5A67D8] hover:bg-slate-100 h-14 px-8 text-lg"
          >
            Ücretsiz Hesap Oluştur
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#5A67D8] flex items-center justify-center">
                  <Baby className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold">NeoBreed AI</span>
              </div>
              <p className="text-slate-400 max-w-md">
                Yapay zeka destekli fetal yüz görüntüleme teknolojisi ile anne-babalara 
                unutulmaz bir deneyim sunuyoruz.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Hızlı Bağlantılar</h4>
              <ul className="space-y-2 text-slate-400">
                <li><a href="#features" className="hover:text-white transition-colors">Özellikler</a></li>
                <li><a href="/about" className="hover:text-white transition-colors">Hakkımızda</a></li>
                <li><a href="/contact" className="hover:text-white transition-colors">İletişim</a></li>
                <li><a href="/login" className="hover:text-white transition-colors">Giriş Yap</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">İletişim</h4>
              <ul className="space-y-2 text-slate-400">
                <li>info@neobreedai.com</li>
                <li>Kartepe / KOCAELİ</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-12 pt-8 text-center text-slate-400">
            <p>© 2026 Konsültan Özel Sağlık Hizmetleri Ltd. Şti. Tüm hakları saklıdır.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
